import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyS, BrandS } from 'atoms/typography';
import { TermsDialog } from 'components/conversionCredit/TermsDialog';
import { FRONTEND_ASSET_HOST } from 'constants/assets';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useMonetaryAmount } from 'hooks/useMonetaryAmount';
import { glossary } from 'lib/glossary';
import { localStorage } from 'lib/storage/localStorage';
import { borderGradientStyle } from 'style/utils';

import type { PromoBanner_conversionCredit } from './__generated__/index.graphql';
import background from './assets/background.webp';

const Root = styled(Horizontal).attrs({ gap: 3 })`
  --border-gradient: var(--c-gradient-discount);
  --border-size: 2px;

  width: 100%;
  ${borderGradientStyle};
  padding: var(--double-unit);
  border-radius: var(--double-and-a-half-unit);
  background:
    no-repeat center / cover url(${background}),
    var(--c-nd-50-opaque);
  & > * {
    position: relative;
  }
`;
const StyledImg = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;
`;
const UnderlinedText = styled(BodyS)`
  text-decoration: underline;
`;
const StyledVertical = styled(Vertical)`
  align-items: flex-start;
`;

type Props = {
  conversionCredit: Nullable<PromoBanner_conversionCredit>;
  leftAligned?: boolean;
};
export const PromoBanner = ({ conversionCredit, leftAligned }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const { maxDiscount, percentageDiscount } = conversionCredit || {};
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const { main } = useAmountWithConversion({
    monetaryAmount: toMonetaryAmount(maxDiscount),
    hideFractionDigits: true,
  });

  if (!conversionCredit) return null;

  const affiliateId = localStorage.getItem('utm_campaign');

  return (
    <>
      <Root center={!leftAligned}>
        <Vertical gap={0.5} center>
          <StyledImg src={`${FRONTEND_ASSET_HOST}/rewards/credit.png`} alt="" />
          <BrandS>{main}</BrandS>
        </Vertical>
        <StyledVertical gap={0.5}>
          <BrandS>
            <FormattedMessage
              id="PromoBanner.title"
              defaultMessage="Welcome offer"
            />
          </BrandS>
          <BodyS color="var(--c-nd-700)">
            <FormattedMessage
              id="PromoBanner.description"
              defaultMessage="Up to <b>{value} OFF*</b>"
              values={{
                percentageDiscount,
                value: maxDiscount ? main : 0,
                b: chunks => {
                  return (
                    <BodyS as="span" color="var(--c-white)" bold>
                      {chunks}
                    </BodyS>
                  );
                },
              }}
            />
          </BodyS>
          <ButtonBase onClick={() => setOpenTermsDialog(true)}>
            <UnderlinedText color="var(--c-nd-700)">
              <FormattedMessage {...glossary.termsApply} />
            </UnderlinedText>
          </ButtonBase>
        </StyledVertical>
      </Root>
      {!!openTermsDialog && (
        <TermsDialog
          onClose={() => setOpenTermsDialog(false)}
          terms={affiliateId ? 'boostedCreditTerms' : 'creditTerms'}
        />
      )}
    </>
  );
};

PromoBanner.fragments = {
  conversionCredit: gql`
    fragment PromoBanner_conversionCredit on ConversionCredit {
      id
      percentageDiscount
      maxDiscount {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<PromoBanner_conversionCredit>,
};
